import type { ExpressionSpecification } from 'maplibre-gl'
import { HOUSEHOLD_COLORS } from '../../lib/mapConfig'

// Shared MapLibre paint for the turf map's count-scaled circles (households, workplaces).
// A fixed 10-20px dot is illegible at metro zoom: hundreds of overlapping discs. Radius and
// stroke therefore grow with zoom; the count still scales the dot at every zoom level.
//
// MapLibre rule: `["zoom"]` may only be the input of a TOP-LEVEL interpolate/step, so zoom is
// the outer interpolate and each zoom stop's output is the data-driven count sub-expression.

/** [count, radius] pairs per zoom stop. The z16 row is the pre-2026-10-23 fixed sizing. */
const RADIUS_BY_ZOOM: ReadonlyArray<readonly [zoom: number, stops: ReadonlyArray<readonly [number, number]>]> = [
  [10, [[1, 2], [5, 2.5], [10, 3]]],
  [13, [[1, 5], [5, 7], [10, 9]]],
  [16, [[1, 10], [5, 15], [10, 20]]],
]

/** Zoom-interpolated circle radius, scaled within each zoom stop by the feature's `countProp`. */
export function zoomScaledCountRadius(countProp: string): ExpressionSpecification {
  const zoomStops = RADIUS_BY_ZOOM.flatMap(([zoom, stops]) => [
    zoom,
    ['interpolate', ['linear'], ['get', countProp], ...stops.flat()],
  ])
  return ['interpolate', ['linear'], ['zoom'], ...zoomStops] as unknown as ExpressionSpecification
}

/** Thin outline at metro zoom, the original 2px outline at street zoom. */
export const ZOOM_SCALED_STROKE_WIDTH: ExpressionSpecification =
  ['interpolate', ['linear'], ['zoom'], 10, 0.5, 16, 2]

/** Legend key for the household dot colours chosen in `householdsToGeoJson`. Same constants,
 *  so the key can never drift from the paint. "Partly" also covers all-visited-but-revisit. */
export const HOUSEHOLD_STATUS_KEY = [
  { key: 'none', label: 'Not visited', color: HOUSEHOLD_COLORS.none_visited },
  { key: 'partial', label: 'Partly / revisit', color: HOUSEHOLD_COLORS.partial_visited },
  { key: 'all', label: 'All visited', color: HOUSEHOLD_COLORS.all_visited },
] as const
